/**
 * apps/automations-portal/src/features/demo/RunTheatre.tsx
 *
 * Staged progress display for a demo run. The backend doesn't send a
 * per-step feed — only a coarse status (PENDING/RUNNING/SUCCEEDED/
 * FAILED/EXPIRED) — so the three stages shown here are derived locally
 * from that status. The middle stage is indicative of the automation
 * working, not a real trace of engine internals.
 *
 * Purely presentational — DemoPage owns polling and passes the current
 * run + phase.
 */
import { Check, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { DemoRun } from '@/lib/types';

/**
 * Orchestration phase, owned by DemoPage. Distinct from `run.status`:
 * covers the moment before a run exists ('starting') and a client-side
 * poll ceiling ('timeout'), neither of which the backend represents.
 * Once a run reaches a terminal status, DemoPage moves to 'done' and
 * this component reads `run.status` to decide what that means.
 */
export type RunPhase = 'idle' | 'starting' | 'polling' | 'done' | 'timeout';

type StageStatus = 'pending' | 'active' | 'done';

interface Stage {
  label: string;
  status: StageStatus;
}

function computeStages(run: DemoRun | null): Stage[] {
  const status = run?.status;
  const isTerminalFailure = status === 'FAILED' || status === 'EXPIRED';
  const isSucceeded = status === 'SUCCEEDED';

  const reading: StageStatus = run ? 'done' : 'active';
  const drafting: StageStatus = !run ? 'pending' : isSucceeded || isTerminalFailure ? 'done' : 'active';
  const finished: StageStatus = isSucceeded ? 'done' : 'pending';

  return [
    { label: 'Reading your text', status: reading },
    { label: 'Drafting with AI', status: drafting },
    { label: 'Done', status: finished },
  ];
}

export function RunTheatre({ run, phase }: { run: DemoRun | null; phase: RunPhase }) {
  if (phase === 'idle') return null;

  if (phase === 'timeout') {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          This is taking longer than expected. Please try again in a moment.
        </CardContent>
      </Card>
    );
  }

  const status = run?.status;
  const isTerminalFailure = status === 'FAILED' || status === 'EXPIRED';
  const stages = computeStages(run);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-3 py-6">
          {stages.map((stage) => (
            <StepRow key={stage.label} label={stage.label} status={stage.status} />
          ))}
        </CardContent>
      </Card>

      {isTerminalFailure && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-destructive">
            {status === 'EXPIRED'
              ? 'This run expired before it finished. Please try again.'
              : 'Something went wrong running that automation. Please try again.'}
          </CardContent>
        </Card>
      )}

      {status === 'SUCCEEDED' && run?.outputText && (
        <Card className="animate-[slideUp_200ms_ease-out]">
          <CardHeader>
            <CardTitle className="text-base">Draft output</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-4 font-serif text-sm leading-relaxed">
              {run.outputText}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StepRow({ label, status }: { label: string; status: StageStatus }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs',
          status === 'done' && 'border-success bg-success text-success-foreground',
          status === 'active' && 'border-primary text-primary',
          status === 'pending' && 'border-border text-muted-foreground',
        )}
      >
        {status === 'done' && <Check className="h-3 w-3" />}
        {status === 'active' && <Loader2 className="h-3 w-3 animate-spin" />}
      </span>
      <span
        className={cn(
          'text-sm',
          status === 'pending' ? 'text-muted-foreground' : 'text-foreground',
          status === 'done' && 'line-through decoration-muted-foreground/40',
        )}
      >
        {label}
      </span>
    </div>
  );
}
